import { limit, orderBy, where } from "firebase/firestore";
import queryString from "query-string";
import React, { useEffect } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { toMeasurementQueryDownloadItem } from "../../api/api.jsx";
import {
    useFlightDocument,
    useMeasurementCollection,
    useMissionDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import { DownloadButton } from "../../components/DownloadButton/index.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import LoadMoreButton from "../../components/LoadMoreButton";
import MeasurementDeletedAlert from "../../components/MeasurementDeletedAlert/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase.js";
import urls from "../../urls.jsx";

export default function ImageListPage() {
    const { organizationId, locationId } = useParams();
    const location = useLocation();
    const { missionId, flightId } = queryString.parse(location.search);

    const resolution = "256x256";
    const measurementsPerPage = 16;
    const maxMeasurementsToLoad = 10000;

    const [mission] = useMissionDocument(organizationId, locationId, missionId);
    const [flight] = useFlightDocument(organizationId, locationId, flightId);
    const nrMeasurements =
        mission?.measurements?.length ?? flight?.measurements?.length ?? 0;

    const measurementsDeletedTimestamp =
        mission?.measurementsDeletedAt ?? flight?.measurementsDeletedAt;

    const [measurementsToLoad, setMeasurementsToLoad] =
        React.useState(measurementsPerPage);

    const [measurementDocs, isLoading] = useMeasurementCollection(
        organizationId,
        locationId,
        [
            ...(missionId ? [where("meta.mission.id", "==", missionId)] : []),
            ...(flightId ? [where("meta.flight.id", "==", flightId)] : []),
            limit(Math.min(measurementsToLoad, maxMeasurementsToLoad)),
            orderBy("event.summary.MEASUREMENT_STARTED", "asc"),
        ],
    );
    const [measurements, setMeasurements] = React.useState([]);
    const { getUserIsAdmin } = useUser();
    const backUrl = missionId
        ? urls.locationMission(organizationId, locationId, missionId)
        : flightId
          ? urls.flight(organizationId, locationId, flightId)
          : urls.location(organizationId, locationId);
    const navigate = useNavigate();

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            navigate(urls.locations(organizationId), { replace: true });
        }
    }, [locationId, organizationId, navigate]);

    // Load measurements images, sort on timestamp
    React.useEffect(() => {
        if (!measurementsDeletedTimestamp) {
            Promise.all(
                measurementDocs.map((measurement) => {
                    const organizationPath = `organizations/${organizationId}`;
                    const locationPath = `${organizationPath}/locations/${locationId}`;
                    const resolutionPath = `${locationPath}/measurements/${resolution}`;
                    const measurementName = `${measurement.id}_${resolution}.jpg`;
                    const measurementPath = `${resolutionPath}/${measurementName}`;

                    return getFirebaseStorageDownloadUrl(measurementPath)
                        .then((url) => {
                            return { ...measurement, thumbnail: url };
                        })
                        .catch(() => {
                            return {
                                ...measurement,
                                thumbnail: "/image_placeholder.png",
                            };
                        });
                }),
            ).then(setMeasurements);
        }
    }, [
        organizationId,
        locationId,
        measurementDocs,
        measurementsDeletedTimestamp,
    ]);

    return (
        <Layout>
            <title>Images | MyCorvus</title>
            {!missionId && flightId && (
                <Breadcrumbs
                    path={breadcrumbPaths.flightImages}
                    flight={flight ?? null}
                />
            )}
            {missionId && !flightId && (
                <Breadcrumbs
                    path={breadcrumbPaths.missionImages}
                    mission={mission ?? null}
                />
            )}
            {!missionId && !flightId && (
                <Breadcrumbs path={breadcrumbPaths.locationImages} />
            )}
            <Card>
                <Title title={"Images"} backButtonUrl={backUrl}>
                    {organizationId &&
                        locationId &&
                        missionId &&
                        !measurementsDeletedTimestamp && (
                            <DownloadButton
                                downloadItems={[
                                    toMeasurementQueryDownloadItem({
                                        organizationId,
                                        locationId,
                                        missionId,
                                    }),
                                ]}
                                checkAvailability={false}
                                filename={`measurements_${missionId}.zip`}
                                buttonText={"Download all"}
                                variant="outline-secondary"
                                icon={<FaDownload className="me-2" />}
                            />
                        )}
                </Title>
            </Card>
            {measurementsDeletedTimestamp ? (
                <MeasurementDeletedAlert
                    deletedTimestamp={measurementsDeletedTimestamp}
                    organizationId={organizationId}
                    locationId={locationId}
                    className="mt-3"
                    deletedText="These images were deleted at"
                />
            ) : (
                <>
                    <Row>
                        {measurements.map((m) => (
                            <Col key={m.id} lg={3}>
                                <Card
                                    as={Link}
                                    to={urls.measurement(
                                        organizationId,
                                        locationId,
                                        missionId,
                                        m.id,
                                    )}
                                    className="mt-4"
                                >
                                    <div style={{ position: "relative" }}>
                                        <Card.Img src={m.thumbnail} />
                                        {getUserIsAdmin() &&
                                            (m.tags || []).length > 0 && (
                                                <h1
                                                    className="h6 mt-1 ms-1"
                                                    style={{
                                                        position: "absolute",
                                                        top: "0",
                                                        left: "0",
                                                        color: "blue",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    🔵
                                                </h1>
                                            )}
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    <LoadMoreButton
                        label="images"
                        load={() => {
                            setMeasurementsToLoad(
                                measurementsToLoad + measurementsPerPage,
                            );
                        }}
                        showLoadAll={true}
                        loadAll={() =>
                            setMeasurementsToLoad(maxMeasurementsToLoad)
                        }
                        isLoading={isLoading}
                        itemsLoaded={measurementDocs?.length || 0}
                        totalItems={nrMeasurements}
                        className="mt-4"
                        disabled={measurementDocs?.length >= nrMeasurements}
                    />
                </>
            )}
        </Layout>
    );
}
